/**
 * ReactionDetails — who reacted to this message, and who has read it
 * (2026-09-19, Miles: on mobile "there's no way" to see who reacted).
 *
 * The chip's `title` was the whole answer, and a touch browser never renders
 * one. This is the surface that answers it on every device: a bottom SHEET on
 * a phone, the same content as an anchored PANEL on a desktop, and a compact
 * HOVER CARD that replaces the native tooltip on a fine pointer. All three read
 * ONE model (reactionDetailsModel), so "who reacted" cannot come to mean three
 * different things depending on where you looked.
 *
 * Deliberately NOT a new overlay primitive. The sheet shell is the thread
 * sheet's own (ThreadSheetScrim / Container / Handle / Header / Title / List —
 * 70dvh, the safe-area pad, the slide-up and its reduced-motion stand-down),
 * and the panel is placed by the reaction picker's own placeReactionPicker, so
 * it lands inside the pane's bounds exactly as the picker does.
 *
 * Portal contract, the same three parts every chat popup here obeys:
 *   1. portaled to `document.body` — the chat pane carries `backdrop-filter`
 *      and `container-type`, either of which makes it the containing block for
 *      a `position: fixed` child, so a rung means nothing without the portal;
 *   2. the layer comes from the one ladder as BODY_PORTAL_Z_INDEX, never a
 *      number typed here;
 *   3. `data-prest0n-portal="true"` — spelled with its VALUE — on the scrim AND
 *      the surface, or the chat's outside-click handler closes the whole pane
 *      on the first tap inside this one.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { HoverCardPortal } from './HoverCardPortal';
import {
  BODY_PORTAL_Z_INDEX,
  ThreadSheetScrim,
  ThreadSheetContainer,
  ThreadSheetHandle,
  ThreadSheetHeader,
  ThreadSheetTitle,
  ThreadSheetList,
} from './styles';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { placeReactionPicker, type PickerAnchor, type PickerRect } from '../../services/messageReactions/pickerPlacement';
import { resolveActiveTab, type ActivityPerson, type ActivityTab } from '../../services/messageReactions/reactionDetailsModel';

export type ReactionDetailsVariant = 'sheet' | 'panel';

export interface ReactionDetailsProps {
  variant: ReactionDetailsVariant;
  tabs: ReadonlyArray<ActivityTab>;
  /** The tab to open on (the seen-by marks open on `seen`); falls back to the first. */
  initialTab?: string | null;
  /** Panel form: where the chips are, and the pane rect to stay inside. */
  anchor?: PickerAnchor | null;
  bounds?: PickerRect | null;
  /** False while the message cannot take a reaction — the remove control stands down. */
  canToggle?: boolean;
  onRemove?: (emoji: string) => void;
  onClose: () => void;
}

type Lang = 'en' | 'es';

/** UI chrome on the viewer's own surface — labels, not Prest0n's voice. */
function labels(lang: Lang) {
  return lang === 'es'
    ? { reactions: 'reacciones', seenBy: 'visto por', all: 'todas', seen: 'vistos', remove: 'quitar', you: 'tú', close: 'cerrar' }
    : { reactions: 'reactions', seenBy: 'seen by', all: 'all', seen: 'seen', remove: 'remove', you: 'you', close: 'close' };
}

const TabStrip = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0 14px 10px;

  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button<{ $active?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? 'var(--brand-gold-text)' : 'rgba(255, 255, 255, 0.14)')};
  background: ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.18)' : 'rgba(255, 255, 255, 0.05)')};
  color: ${(p) => (p.$active ? 'var(--brand-gold-hover)' : 'rgba(255, 255, 255, 0.72)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
  }
`;

const TabEmoji = styled.span`
  font-size: 0.95rem;
  line-height: 1;
`;

const PersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 10px;
  min-height: 40px;

  /* A phone control: a full finger target (ComposerPlusMenu's 44px rule). */
  @media (pointer: coarse) {
    min-height: 44px;
  }
`;

const PersonDot = styled.span<{ $assistant?: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: ${(p) => (p.$assistant ? 'var(--brand-gold-text)' : 'var(--brand-text-secondary)')};
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.14);
`;

const PersonName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.86rem;
  color: rgba(255, 255, 255, 0.88);
`;

const PersonEmojis = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  gap: 3px;
  font-size: 1rem;
  line-height: 1;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover { color: var(--brand-gold-hover); border-color: var(--brand-gold-text); }
  &:focus-visible { outline: 2px solid var(--brand-gold); outline-offset: 1px; }
`;

function PersonRows({
  tab, lang, canToggle, onRemove,
}: { tab: ActivityTab; lang: Lang; canToggle: boolean; onRemove?: (emoji: string) => void }): React.ReactElement {
  const t = labels(lang);
  return (
    <>
      {tab.people.map((person: ActivityPerson) => (
        <PersonRow key={`${tab.key}:${person.uid}`} data-reaction-person={person.uid}>
          <PersonDot aria-hidden="true" $assistant={person.assistant}>{person.glyph}</PersonDot>
          <PersonName>{person.label}</PersonName>
          {person.emojis.length > 0 && (
            <PersonEmojis aria-hidden="true">{person.emojis.map((e) => <span key={e}>{e}</span>)}</PersonEmojis>
          )}
          {person.isSelf && tab.kind === 'emoji' && tab.emoji && canToggle && onRemove && (
            <RemoveButton
              type="button"
              data-reaction-remove={tab.emoji}
              onClick={() => onRemove(tab.emoji as string)}
            >
              {t.remove}
            </RemoveButton>
          )}
        </PersonRow>
      ))}
    </>
  );
}

export function ReactionDetails({
  variant, tabs, initialTab, anchor, bounds, canToggle = true, onRemove, onClose,
}: ReactionDetailsProps): React.ReactElement | null {
  const lang: Lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  const t = labels(lang);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);
  const [active, setActive] = useState<string | null>(() => resolveActiveTab(tabs, initialTab));

  // Reference-counted shared lock — nests cleanly with the fullscreen
  // takeover's own (the thread sheet's contract).
  useBodyScrollLock(variant === 'sheet');

  // The last reaction on the message was taken away while this was open (very
  // often BY the remove button in here). Close, rather than leaving a surface
  // rendering nothing and a scrim the host still believes is up — which would
  // otherwise spring back into view the moment somebody reacted again.
  useEffect(() => {
    if (tabs.length === 0) onClose();
  }, [tabs.length, onClose]);

  // The tabs are recomputed live while this is open (someone may react while
  // you are reading it): keep the chosen tab when it survives, fall back when
  // its last reaction was taken away.
  useEffect(() => {
    setActive((current) => resolveActiveTab(tabs, current ?? initialTab));
  }, [tabs, initialTab]);

  // Panel form: measured, then placed by the picker's own rule — above the
  // chips when it fits, else below, always inside the pane's bounds.
  useLayoutEffect(() => {
    if (variant !== 'panel' || !anchor) return;
    const el = surfaceRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = placeReactionPicker(
      anchor,
      { width: rect.width || 280, height: rect.height || 220 },
      { width: window.innerWidth, height: window.innerHeight },
      bounds ?? null,
    );
    setPlacement((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : { top: next.top, left: next.left }));
  }, [variant, anchor, bounds, tabs, active]);

  // Escape closes, capture phase — the chat's own Escape handling must not
  // also fire (ThreadListSheet's contract).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Panel form only: the anchor was measured at open time, so a resize makes
  // it stale — close rather than float loose.
  useEffect(() => {
    if (variant !== 'panel') return undefined;
    const onResize = () => onClose();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [variant, onClose]);

  // An aria-modal dialog that never takes focus leaves a keyboard user tabbing
  // the chrome BEHIND the scrim; the trigger gets its focus back on close.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (document.querySelector<HTMLElement>('[data-reaction-tab]')
      || document.querySelector<HTMLElement>('[data-reaction-details]')
      || surfaceRef.current)?.focus();
    return () => { previouslyFocused?.focus(); };
  }, []);

  const activeTab = useMemo(() => tabs.find((tab) => tab.key === active) ?? tabs[0] ?? null, [tabs, active]);
  const onlySeen = tabs.length === 1 && tabs[0]?.kind === 'seen';
  const title = onlySeen ? t.seenBy : t.reactions;

  if (typeof document === 'undefined' || tabs.length === 0 || !activeTab) return null;

  const tabLabel = (tab: ActivityTab): string => {
    if (tab.kind === 'all') return t.all;
    if (tab.kind === 'seen') return t.seen;
    return '';
  };

  return createPortal(
    <>
      <ThreadSheetScrim
        data-reaction-details-scrim=""
        data-prest0n-portal="true"
        $transparent={variant === 'panel'}
        onClick={onClose}
      />
      <ThreadSheetContainer
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-reaction-details=""
        data-reaction-details-variant={variant}
        data-prest0n-portal="true"
        $variant={variant}
        style={variant === 'panel'
          ? {
            top: placement?.top ?? -9999,
            left: placement?.left ?? -9999,
            right: 'auto',
            bottom: 'auto',
            width: 'min(320px, calc(100vw - 24px))',
            maxHeight: 'min(60vh, 420px)',
            visibility: placement ? 'visible' : 'hidden',
          }
          : undefined}
      >
        {variant === 'sheet' && <ThreadSheetHandle aria-hidden="true" />}
        <ThreadSheetHeader>
          <ThreadSheetTitle>{title}</ThreadSheetTitle>
        </ThreadSheetHeader>
        {tabs.length > 1 && (
          <TabStrip role="tablist" aria-label={title}>
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={tab.key === activeTab.key}
                data-reaction-tab={tab.key}
                $active={tab.key === activeTab.key}
                onClick={() => setActive(tab.key)}
              >
                {tab.emoji && <TabEmoji aria-hidden="true">{tab.emoji}</TabEmoji>}
                {tabLabel(tab)}
                {` ${tab.count}`}
              </Tab>
            ))}
          </TabStrip>
        )}
        <ThreadSheetList data-reaction-details-list="">
          <PersonRows tab={activeTab} lang={lang} canToggle={canToggle} onRemove={onRemove} />
        </ThreadSheetList>
      </ThreadSheetContainer>
    </>,
    document.body,
  );
}

/* The card's surface, the portal, the placement, the ladder rung and the click
   marker all live in HoverCardPortal now (2026-09-21) — a second consumer (a
   retired mention row) needed the same shell, and a third (the bell) is coming.
   The names still wrap (see HoverNames), so the emoji sits at the top of the
   block rather than floating in the middle of a three-line card. */

const HoverBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/**
 * Everyone the model named, wrapped (Miles, 2026-09-20). This used to be one
 * nowrap line ending in an ellipsis, which was right when it carried three
 * names and is wrong now that it carries the answer. Wrapping is granted to the
 * LINE, never inside a name — the meta row's own "Prest0n" contract.
 */
const HoverNames = styled.span`
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
`;

/**
 * Stated only when the card could not name everyone — the one place a hint is
 * needed, shown at the moment of wanting it. This is the whole visible
 * discoverability budget: Miles ruled out another element, so the gesture is
 * named in words inside a surface that already exists, never in the meta row.
 */
const HoverHint = styled.span`
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.72rem;
`;

export interface ReactionHoverCardProps {
  anchor: PickerAnchor;
  bounds?: PickerRect | null;
  emoji: string;
  names: string;
  /** The model could not name everyone ⇒ the card states the gesture that can. */
  truncated?: boolean;
  /** The reader's own language, for that one line. */
  lang?: 'en' | 'es';
}

/**
 * The fine-pointer replacement for the native tooltip: instant, styled, and
 * truncated by the model rather than by the OS. Never on a coarse pointer —
 * there is no hover there, and the sheet is that device's answer.
 *
 * Since 2026-09-20 it names EVERYONE it can (Miles), which makes hover the
 * complete answer on a desktop in every ordinary thread — and where it cannot,
 * it says how to reach the rest, because the › pill that used to say so is gone.
 */
export function ReactionHoverCard({ anchor, bounds, emoji, names, truncated, lang = 'en' }: ReactionHoverCardProps): React.ReactElement | null {
  if (typeof document === 'undefined' || !names) return null;

  return (
    <HoverCardPortal
      anchor={anchor}
      bounds={bounds}
      zIndex={BODY_PORTAL_Z_INDEX}
      markers={{ 'data-reaction-hover-card': '' }}
      // This card's content is NOT duplicated in the chip's accessible name
      // (the chip names the emoji and the count), so unlike the retired-row
      // card it is not hidden from assistive tech.
      ariaHidden={false}
      placeKey={names}
    >
      <span aria-hidden="true">{emoji}</span>
      <HoverBody>
        <HoverNames>{names}</HoverNames>
        {truncated && (
          <HoverHint data-reaction-hover-hint="">
            {lang === 'es' ? 'mantén pulsado o haz clic derecho para verlos a todos' : 'hold or right-click to see everyone'}
          </HoverHint>
        )}
      </HoverBody>
    </HoverCardPortal>
  );
}

export default ReactionDetails;
